import React, { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import useContext from '../../../../../hooks/useContext';
import formatPrice from '../../../../../utils/price';

interface IPriceContext {
  displayPrice: string;
  hasNoPrice: boolean;
  isSale: boolean;
  oldDisplayPrice?: string;
  oldPrice: number;
  price: number;
  setPrice: (price: IPrice) => void;
}

const PriceContext = createContext<IPriceContext | undefined>(undefined);

interface IPriceProvider {
  before?: number;
  children: ReactNode;
  price?: number;
  pilPrice?: number;
  referencePrice: number;
}

const PriceProvider = ({
  children,
  price: initialPrice,
  before: initialBefore,
  referencePrice: initialReferencePrice,
}: IPriceProvider) => {
  const [priceData, setPriceData] = useState<Pick<IPrice, 'price' | 'before' | 'referencePrice'>>({
    price: initialPrice || 0,
    before: initialBefore || 0,
    referencePrice: initialReferencePrice,
  });

  useEffect(() => {
    setPriceData({
      price: initialPrice || 0,
      before: initialBefore || 0,
      referencePrice: initialReferencePrice,
    });
  }, [initialPrice, initialBefore, initialReferencePrice]);

  const { price, before: oldPrice, referencePrice } = priceData;
  const isSale = oldPrice > 0 && oldPrice !== price;

  const displayPrice = useMemo(() => formatPrice(price), [price]);
  const oldDisplayPrice = useMemo(
    () => (isSale ? formatPrice(referencePrice ?? oldPrice) : undefined),
    [isSale, oldPrice],
  );

  const context = useMemo(
    () => ({
      displayPrice,
      hasNoPrice: !price,
      isSale,
      oldDisplayPrice,
      oldPrice: oldPrice,
      price,
      setPrice: setPriceData,
    }),
    [price, oldPrice],
  );

  return <PriceContext.Provider value={context}>{children}</PriceContext.Provider>;
};

export default PriceProvider;

export const usePrice = () => useContext(PriceContext);
